import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Card, Alert } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import "../style/forgotPassword.css";
import APIFunction from "../util/api";
import { INFO_STATEMENTS, ERROR_MESSAGES } from "../util/constants";

const ResetPasswordForm = () => {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showAlert, setShowAlert] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [alertMsg, setAlertMsg] = useState('');
  const [alertType, setAlertType] = useState(INFO_STATEMENTS.SUCCESS);
  const [token, setToken] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Extract token from URL parameters
    const urlParams = new URLSearchParams(location.search);
    const tokenFromUrl = urlParams.get('token');
    
    if (!tokenFromUrl) {
      setAlertMsg(ERROR_MESSAGES.INVALID_RESET_LINK);
      setAlertType(INFO_STATEMENTS.DANGER);
      setShowAlert(true);
      setIsVerifying(false);
      return;
    }

    setToken(tokenFromUrl);
    setTokenValid(true);
    setIsVerifying(false);
  }, [location]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validatePasswords = () => {
    if (formData.newPassword.length < 8) {
      setAlertMsg(ERROR_MESSAGES.PASSWORD_LENGTH_ERROR);
      setAlertType(INFO_STATEMENTS.DANGER);
      setShowAlert(true);
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setAlertMsg(ERROR_MESSAGES.PASSWORD_MISSMATCH);
      setAlertType(INFO_STATEMENTS.DANGER);
      setShowAlert(true);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowAlert(false);
    
    if (!validatePasswords()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const payload = {
        token: token,
        newPassword: formData.newPassword
      };
      
      await APIFunction.resetPassword(payload);
      setAlertMsg(INFO_STATEMENTS.PASSWORD_RESET_SUCCESSFUL);
      setAlertType(INFO_STATEMENTS.SUCCESS);
      setFormData({ newPassword: '', confirmPassword: '' });
      setShowAlert(true);
      
      // Redirect to login page after 3 seconds
      // setTimeout(() => {
      //   navigate('/login');
      // }, 3000);
      
    } catch (err) {
      console.error(err);
      setAlertMsg(err.message || ERROR_MESSAGES.PASSWORD_RESET_FAILED);
      setAlertType(INFO_STATEMENTS.DANGER);
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isVerifying) {
    return (
      <section className="forgotPasswordSection">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <Card className="shadow-lg border-0">
                <Card.Body className="p-5 text-center">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted">Verifying reset token...</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    );
  }

  if (!tokenValid) {
    return (
      <section className="forgotPasswordSection">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <Card className="shadow-lg border-0">
                <Card.Body className="p-5">
                  <div className="text-center mb-4">
                    <h2 className="fw-bold text-danger">Invalid Reset Link</h2>
                  </div>
                  
                  <Alert variant="danger" className="mb-4">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {alertMsg}
                  </Alert>
                  
                  <div className="text-center">
                    <Button 
                      variant="primary" 
                      onClick={() => navigate('/forgot-password')}
                      className="me-3"
                    >
                      Request New Reset Link
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => navigate('/login')}
                    >
                      Back to Login
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    );
  }

  return (
    <section className="forgotPasswordSection">
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow-lg border-0">
              <Card.Body className="p-5">
                <div className="text-center mb-4">
                  <h2 className="fw-bold text-secondary">Reset Your Password</h2>
                  <p className="text-muted">
                    Enter your new password below.
                  </p>
                </div>
                
                {showAlert && (
                  <Alert variant={alertType} className="mb-4">
                    <i className={`bi ${alertType === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle'} me-2`}></i>
                    {alertMsg}
                  </Alert>
                )}
                
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label>New Password</Form.Label>
                    <Form.Control
                      type="password"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleChange}
                      placeholder="Enter your new password"
                      required
                      disabled={isSubmitting}
                      minLength={6}
                    />
                    <Form.Text className="text-muted">
                      Password must be at least 8 characters long.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Confirm New Password</Form.Label>
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm your new password"
                      required
                      disabled={isSubmitting}
                      minLength={6}
                    />
                  </Form.Group>
                  
                  <div className="d-grid mb-4">
                    <Button 
                      variant="primary" 
                      type="submit" 
                      size="lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Resetting Password...
                        </>
                      ) : (
                        <>
                          Reset Password <i className="bi bi-key"></i>
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-muted">
                      Remember your password? <a href="/login" className="text-primary">Back to Login</a>
                    </p>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default ResetPasswordForm;
